/**
 * /dashboard/organizations/:id (screenshots/60–65, interactions 66; brief §2.10).
 * Tabs: Overview · Legislation lobbied · Money · Lobbyists · Filings · Votes & forecast (?tab=).
 * Rules kept here and in logic.ts (tests/screens.test.ts):
 *   - money always breaks down by source (company, employees, lobbyists), with a Company-only switch and the
 *     overlap note whenever lobbyist money is part of a total;
 *   - money-linked votes need a STATED position; an inferred one only feeds forecasts and is labelled;
 *   - forecasts are forecastVote() output computed in the Worker from real inputs.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, Calendar, Check, ChevronsUpDown, FileText, Info, Landmark, Plus, ScrollText, Search, Share, Sparkles } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { Face } from "../components/shared/Face";
import { List, ListRow, RowCell, RowLead, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";
import { Kbd, TabBar, ViewSwitch } from "../components/shared/ui";
import { TabErrorBoundary } from "../components/shared/States";
import { ForecastMeter, Roll } from "../forecast/ForecastViews";
import { navigate, plainClick } from "../app/router";
import { toastStore } from "../stores/toastStore";
import { duration, easing, prefersReducedMotion, recipes, springEasing, reducedFallback } from "../motion";
import { can, type OrgRole, type UserGroup } from "../nav/navConfig";
import { amountFor, andList, asFiled, canComputeMoneyLinked, fullDate, kindNoun, lobbyistShare, memberAmount, needsOverlapNote, overlapPeek, positionCounts, quarterLabel, total, usd, whyThisMatters, type MoneyView } from "./logic";
import { initials, shortName } from "./build";
import { orgStore, useOrgs } from "./orgStore";
import type { ClientProfile, LobbiedItem, OrgForecastRow, ProfileLobbyist } from "./types";
import { useDismiss } from "../hooks/useDismiss";
import { Crumbs, sectionCrumb } from "../app/crumbs";

export type OrgTab = "overview" | "items" | "money" | "lobbyists" | "filings" | "votes";
export const ORG_TABS: OrgTab[] = ["overview", "items", "money", "lobbyists", "filings", "votes"];

const personHref = (personId: string, orgId: string) => `/dashboard/people/${encodeURIComponent(personId)}/money?org=${encodeURIComponent(orgId)}`;
const go = (href: string) => (e: React.MouseEvent) => { if (!plainClick(e)) return; e.preventDefault(); navigate(href); };

export function OrganizationProfile({ group, role, tab, onTab }: { group: UserGroup; role: OrgRole | null; tab: OrgTab; onTab: (t: OrgTab) => void }) {
  const snap = useOrgs((s) => s.profile);
  const [view, setView] = useState<MoneyView>("all");
  if (!snap) return <p className="empty-panel">This organization isn't available yet.</p>;
  const org = snap.org;
  const t = total(org.money);
  const team = group !== "resident";
  const tabs = ([
    { id: "overview", label: "Overview" }, { id: "items", label: "Legislation lobbied", count: org.items.length },
    { id: "money", label: "Money", count: usd(t) }, { id: "lobbyists", label: "Lobbyists", count: org.lobbyists.length },
    { id: "filings", label: "Filings", count: org.filings }, { id: "votes", label: "Votes & forecast" },
  ] as { id: OrgTab; label: string; count?: string | number }[]).map((x) => (x.id === tab ? { id: x.id, label: x.label } : x));
  return (
    <div className="page og-page og-profile">
      <Crumbs trail={[sectionCrumb("organizations"), { label: org.name }]} />
      <header className="og-detail-head">
        <span className="og-logo og-logo--lg" aria-hidden>{initials(org.name)}</span>
        <div className="og-detail-head__id">
          <div className="og-detail-head__name"><h1>{org.name}</h1></div>
          <p>{[org.kind, "registered with the Board of Ethics", `${org.lobbyists.length} lobbyist${org.lobbyists.length === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="og-detail-head__actions">
          {can(group, role, "briefMe") && <a className="btn btn--secondary og-head-btn" href={`/dashboard/organizations/${encodeURIComponent(org.id)}/brief`} onClick={go(`/dashboard/organizations/${encodeURIComponent(org.id)}/brief`)}><Sparkles size={15} aria-hidden />Brief me</a>}
          <button className="btn btn--secondary og-head-btn" onClick={() => { navigator.clipboard?.writeText(location.href); toastStore.getState().show({ message: "Link copied" }); }}><Share size={15} aria-hidden />Share</button>
          <FollowButton following={snap.following} />
        </div>
      </header>
      <dl className="og-ledger">
        {([[org.filings.toLocaleString("en-US"), "Lobbying filings"], [String(org.lobbyists.length), "Lobbyists"], [String(org.legislationCount), "Legislation lobbied"],
          [usd(t), "To alderpersons since 2019"], [String(org.alderpersonsFunded), "Alderpersons funded"]] as const).map(([v, l]) => (
          <div key={l}><dd>{v}</dd><dt>{l}</dt></div>))}
      </dl>
      <TabBar label="Organization" active={tab} onChange={onTab} tabs={tabs} />
      <TabErrorBoundary tabLabel={tabs.find((x) => x.id === tab)?.label ?? tab}>
        <div className="tab-panel" key={tab} role="tabpanel">
          {tab === "overview" && <Overview org={org} view={view} meetingLog={can(group, role, "meetingLog")} onTab={onTab} />}
          {tab === "items" && <Items org={org} team={team && (role === "owner" || role === "admin")} />}
          {tab === "money" && <Money org={org} view={view} setView={setView} />}
          {tab === "lobbyists" && <Lobbyists org={org} />}
          {tab === "filings" && <Filings org={org} />}
          {tab === "votes" && <Votes org={org} forecasts={snap.forecasts} />}
        </div>
      </TabErrorBoundary>
    </div>
  );
}

/** Follow / Following, with a spring pop when it flips (screenshots/66 #5). */
function FollowButton({ following }: { following: boolean }) {
  const ref = useRef<HTMLButtonElement>(null);
  const pulse = useOrgs((s) => s.followPulse);
  useEffect(() => {
    if (!pulse || !ref.current) return;
    if (prefersReducedMotion()) { ref.current.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: reducedFallback.duration }); return; }
    const s = springEasing();
    ref.current.animate([{ transform: "scale(0.92)" }, { transform: "scale(1)" }], { duration: s.duration, easing: s.easing });
  }, [pulse]);
  return (
    <button ref={ref} className={`btn ${following ? "btn--primary" : "btn--secondary"} og-head-btn og-follow`} aria-pressed={following}
      onClick={() => void orgStore.getState().setFollowing(!following)}>
      {following ? <Check size={15} aria-hidden /> : <Plus size={15} aria-hidden />}{following ? "Following" : "Follow"}
    </button>
  );
}

const TwoCol = ({ main, side }: { main: ReactNode; side: ReactNode }) => (
  <div className="cols og-cols"><div className="cols__main og-main">{main}</div><aside className="cols__side">{side}</aside></div>
);

/** Hover/focus popover after a delay (Peek recipe: 300 ms intent; 120 ms for chips). */
function Popover({ trigger, children, delay = 300, className }: { trigger: ReactNode; children: ReactNode; delay?: number; className?: string }) {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open || !card.current) return;
    card.current.animate(prefersReducedMotion() ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 0, transform: "scale(0.98)" }, { opacity: 1, transform: "none" }],
      { duration: prefersReducedMotion() ? reducedFallback.duration : duration.fast, easing: easing.enter });
  }, [open]);
  const show = () => { window.clearTimeout(timer.current); timer.current = window.setTimeout(() => setOpen(true), delay); };
  const hide = () => { window.clearTimeout(timer.current); timer.current = window.setTimeout(() => setOpen(false), recipes.stepperPopover.duration); };
  return (
    <span className={`og-pop${className ? ` ${className}` : ""}`} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      {trigger}
      {open && <div ref={card} className="og-pop__card" role="tooltip">{children}</div>}
    </span>
  );
}

/** "Where the $45,850 comes from" (Overview: by source; Money tab: company + each lobbyist). */
function WhereCard({ org, byLobbyist }: { org: ClientProfile; byLobbyist?: boolean }) {
  const t = total(org.money);
  const pct = (n: number) => `${(n / Math.max(1, t)) * 100}%`;
  const lob = org.lobbyists.filter((l) => l.toAlderpersons > 0);
  const share = lobbyistShare(org.money);
  const rows: { cls: string; label: ReactNode; amount: number }[] = byLobbyist
    ? [{ cls: "is-company", label: "Company", amount: org.money.company },
       ...(org.money.employees ? [{ cls: "is-employees", label: "Employees", amount: org.money.employees }] : []),
       ...lob.map((l) => ({ cls: "is-lobbyists", amount: l.toAlderpersons, label: <LobbyistPeek l={l} here={org.name}>{l.name} (lobbyist)</LobbyistPeek> }))]
    : [{ cls: "is-company", label: "Company", amount: org.money.company }, { cls: "is-employees", label: "Employees", amount: org.money.employees }, { cls: "is-lobbyists", label: "Its lobbyists", amount: org.money.lobbyists }];
  return (
    <section className="og-card">
      <h3>Where the {usd(t) === "—" ? "$0" : usd(t)} comes from</h3>
      <div className="og-stack" role="img" aria-label={`Company ${usd(org.money.company)}, employees ${usd(org.money.employees)}, lobbyists ${usd(org.money.lobbyists)}`}>
        {org.money.company > 0 && <span className="is-company" style={{ width: pct(org.money.company) }} />}
        {org.money.employees > 0 && <span className="is-employees" style={{ width: pct(org.money.employees) }} />}
        {org.money.lobbyists > 0 && <span className="is-lobbyists" style={{ width: pct(org.money.lobbyists) }} />}
      </div>
      <div className="og-legend">{rows.map((r, i) => (
        <div key={i} className="og-legend__row"><span><i className={`og-swatch ${r.cls}`} />{r.label}</span><b>{r.amount ? usd(r.amount) : "$0"}</b></div>))}</div>
      {!byLobbyist && org.money.lobbyists > 0 && lob.length > 0 && (
        <p className="og-card__note">{lob.length === 1 ? `${usd(org.money.lobbyists)} is ${lob[0].name}'s own giving${lob[0].clients > 1 ? `, also counted under their ${lob[0].clients - 1} other clients` : ""}.` : `${share > 0.9 ? "Almost all of this is" : "Part of this is"} ${andList(lob.map((l) => l.name))}'s own giving.`}</p>
      )}
    </section>
  );
}

/** Lobbyist overlap peek (screenshots/66 #2): every other client their money is also counted under. */
function LobbyistPeek({ l, here, children }: { l: ProfileLobbyist; here: string; children: ReactNode }) {
  const text = overlapPeek(l, here);
  if (!text) return <>{children}</>;
  return (
    <Popover className="og-pop--peek" trigger={<span className="og-peek-trigger" tabIndex={0}>{children}</span>}>
      <b>{l.name} · {usd(l.toAlderpersons)}</b><p>{text}</p>
    </Popover>
  );
}

function Overview({ org, view, meetingLog, onTab }: { org: ClientProfile; view: MoneyView; meetingLog: boolean; onTab: (t: OrgTab) => void }) {
  const unclear = org.items.every((i) => i.basis !== "stated");
  const inferred = org.items.filter((i) => i.basis === "inferred");
  const named = [...new Set(inferred.flatMap((i) => i.namedProposals ?? []))];
  const funded = [...org.funded].sort((a, b) => memberAmount(b, view) - memberAmount(a, view)).slice(0, 5);
  return <TwoCol
    main={<div className="og-overview">
      {org.interests && <section className="og-care"><div className="og-section-title"><h2>What they care about</h2><span className="ai-chip"><Sparkles size={12} aria-hidden />Written by AI from filings</span></div><p className="og-lede-lg">{org.interests}</p></section>}
      {org.asks.length > 0 && <section><span className="og-eyebrow">What they lobby for</span><div className="og-asks">{org.asks.map((a) => <div key={a.title} className="og-ask"><b>{a.title}</b><p>{a.detail}</p></div>)}</div></section>}
      {org.affects.length > 0 && <section><span className="og-eyebrow">Who it affects</span><dl className="og-affects">{org.affects.map((a) => <div key={a.group}><dt>{a.group}</dt><dd>{a.detail}</dd></div>)}</dl></section>}
      {unclear && org.items.length > 0 && (
        <div className="og-banner" role="note"><Info size={16} aria-hidden />
          <div><b>No filing states a position on a specific record</b>
            <p>{named.length ? `Their filings ${inferred[0].position === "support" ? "support" : "oppose"} ${andList(named.map((n) => `"${n}"`))} by name, but none name a record number with a position. ` : ""}{inferred.length ? "Forecasts for this client use an inferred position and say so." : ""}</p></div>
        </div>
      )}
    </div>}
    side={<>
      <WhereCard org={org} />
      <section className="og-card">
        <h3>Most funded alderpersons</h3>
        <List variant="plain" className="og-funded">{funded.map((m) => (
          <ListRow key={m.personId || m.name} className="og-funded__row" href={m.personId ? personHref(m.personId, org.id) : undefined}>
            <RowLead><Face name={m.name} photoUrl={m.photoUrl} size={32} /></RowLead>
            <RowMain min={100}><RowTitle className="og-funded__name">{m.name}</RowTitle></RowMain>
            <RowTrail><b>{usd(memberAmount(m, view))}</b></RowTrail>
          </ListRow>))}</List>
        {org.alderpersonsFunded > 0 && <button className="link-arrow og-link" onClick={() => onTab("money")}>All {org.alderpersonsFunded} alderpersons<ArrowRight size={14} aria-hidden /></button>}
      </section>
      {meetingLog && <MeetingLog />}
    </>} />;
}

/** Offices: meetings with this organization, team notes only (screenshots/66 "Meeting log"; not designed beyond that card, kept minimal). */
function MeetingLog() {
  const log = useOrgs((s) => s.profile?.meetingLog);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), who: "", note: "" });
  return (
    <section className="og-card">
      <h3>Meeting log</h3>
      {(log ?? []).map((e) => <div key={e.id} className="og-log"><small>{fullDate(e.date)}{e.who ? ` · ${e.who}` : ""}</small><p>{e.note}</p></div>)}
      {adding ? (
        <form className="og-log-form" onSubmit={async (e) => { e.preventDefault(); if (f.note.trim() && await orgStore.getState().addMeeting(f)) { setAdding(false); setF({ ...f, who: "", note: "" }); } }}>
          <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} aria-label="Date" />
          <input value={f.who} onChange={(e) => setF({ ...f, who: e.target.value })} aria-label="Who" />
          <textarea rows={3} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} aria-label="Note" />
          <button className="btn btn--primary" type="submit"><Check size={14} aria-hidden />Save</button>
        </form>
      ) : <button className="link-arrow og-link" onClick={() => setAdding(true)}><Plus size={14} aria-hidden />Log a meeting</button>}
    </section>
  );
}

/** Position chip that opens the filing wording it came from (screenshots/66 #3). */
function PositionChip({ item, mode }: { item: LobbiedItem; mode: "table" | "inferred" }) {
  const label = mode === "inferred" ? `${item.position === "oppose" ? "Opposes" : "Supports"} · inferred`
    : item.basis === "stated" ? (item.position === "oppose" ? "Opposes" : "Supports") : "Unclear";
  const tone = mode === "inferred" || item.basis === "stated" ? (item.position === "oppose" ? " og-chip--negative" : " og-chip--positive") : "";
  const chip = <button className={`og-chip og-chip--btn${tone}`}>{label}</button>;
  if (item.basis === "stated" && mode === "table") return chip;
  const quote = mode === "inferred" ? item.inferredQuote ?? item.namedProposals?.map((n) => `"${n}"`).join(", ") : item.evidence;
  return (
    <Popover delay={120} className="og-pop--chip" trigger={chip}>
      <span className="og-eyebrow">From filings</span>
      <p>{quote && mode === "inferred" ? `"${quote.replace(/^"|"$/g, "")}" ` : quote ? `${quote} ` : ""}{mode === "inferred" ? `No filing names ${item.recordId} with a position.` : ""}</p>
    </Popover>
  );
}

function Items({ org, team }: { org: ClientProfile; team: boolean }) {
  const inferred = org.items.filter((i) => i.basis === "inferred");
  const focus = org.forecastItem?.recordId;
  return <TwoCol
    main={<>
      <table className="og-table og-table--items">
        <thead><tr><th>Item</th><th className="num">Filings</th><th>Position</th><th>Evidence</th></tr></thead>
        <tbody>{org.items.map((i) => (
          <tr key={i.recordId} data-focus={i.recordId === focus && i.basis === "inferred" ? "" : undefined}>
            <td><a className="og-item" href={`/dashboard/legislation/${encodeURIComponent(i.recordId)}`}><span className="og-record"><span className="og-record__icon"><ScrollText size={12} aria-hidden /></span>{i.recordId}</span><b>{i.title}</b></a></td>
            <td className="num">{i.filings}</td>
            <td><PositionChip item={i} mode="table" /></td>
            <td className="og-evidence">{i.evidence}{i.matchedBy === "wording" && <em> · matched by similar wording</em>}</td>
          </tr>))}</tbody>
      </table>
    </>}
    side={<>
      {inferred.map((i) => (
        <section key={i.recordId} className="og-card">
          <h3>Inferred for forecasts</h3>
          <p className="og-card__body">For {i.recordId} we infer {i.position === "oppose" ? "Opposes" : "Supports"}{i.namedProposals?.length ? `, because their filings ${i.position === "oppose" ? "oppose" : "support"} ${andList(i.namedProposals.map((n) => `"${n}"`))}` : ""}. Every forecast that uses it is labelled "inferred".</p>
          <div className="og-inferred-row"><PositionChip item={i} mode="inferred" />{team && <Override item={i} />}</div>
          {i.override && <p className="og-card__note">Your team's forecasts use {i.override.position === "oppose" ? "Opposes" : "Supports"} (override).</p>}
        </section>))}
      <section className="og-note"><h3>How filings are matched</h3><p>Lobbying filings are matched to legislation by record number, address, the client's name, or similar wording. Wording matches are weaker and labelled that way.</p></section>
    </>} />;
}

/** Team override of an inferred position (labelled; saves with an Undo toast). */
function Override({ item }: { item: LobbiedItem }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useDismiss(ref, open, () => setOpen(false));
  return (
    <span className="og-override" ref={ref}>
      <button className="link-arrow og-link" aria-expanded={open} onClick={() => setOpen((o) => !o)}>Team override<ArrowRight size={14} aria-hidden /></button>
      {open && (
        <div className="og-pop__card og-override__menu" role="menu">
          {(["support", "oppose"] as const).map((p) => <button key={p} role="menuitemradio" aria-checked={item.override?.position === p} onClick={() => { setOpen(false); void orgStore.getState().setOverride(item.recordId, p); }}>{p === "support" ? "Supports" : "Opposes"}</button>)}
          {item.override && <button role="menuitem" onClick={() => { setOpen(false); void orgStore.getState().setOverride(item.recordId, null); }}>Use the inferred position</button>}
        </div>
      )}
    </span>
  );
}

/** Money tab (screenshots/62). Switching the view re-scales every bar in place; totals roll (66 #1). */
function Money({ org, view, setView }: { org: ClientProfile; view: MoneyView; setView: (v: MoneyView) => void }) {
  const [all, setAll] = useState(false);
  const rows = org.funded;                                         // order stays put; bars re-scale
  const max = Math.max(1, ...rows.map((r) => memberAmount(r, view)));
  const shown = all ? rows : rows.slice(0, 12);
  return <TwoCol
    main={<>
      <div className="og-stats">
        <div><Roll value={usd(amountFor(org.money, view)) === "—" ? "$0" : usd(amountFor(org.money, view))} /><span>{view === "all" ? "to alderpersons since 2019" : "from the company and employees"}</span></div>
        <div><b>{usd(org.money.company) === "—" ? "$0" : usd(org.money.company)}</b><span>from the company</span></div>
        <div><b>{usd(org.money.lobbyists) === "—" ? "$0" : usd(org.money.lobbyists)}</b><span>from its lobbyists</span></div>
        <div><b>{org.alderpersonsFunded}</b><span>alderpersons funded</span></div>
      </div>
      <div className="og-section-title og-section-title--split">
        <span className="og-section-title"><h2>Alderpersons funded since 2019</h2><span className="count-badge">{org.alderpersonsFunded}</span></span>
        <ViewSwitch label="Money view" value={view} onChange={setView} options={[{ id: "all", label: "All sources" }, { id: "company", label: "Company only" }]} />
      </div>
      <List variant="plain" className="og-hbars">{shown.map((r) => { const v = memberAmount(r, view); return (
        <ListRow key={r.personId || r.name} className="og-hbars__row" href={r.personId ? personHref(r.personId, org.id) : undefined}>
          <RowCell width={172}><RowTitle className="og-hbars__name">{r.name}</RowTitle></RowCell>
          <RowCell width={70}><RowSub className="og-hbars__ward">Ward {r.ward}</RowSub></RowCell>
          <RowMain min={40} className="og-hbars__track"><span style={{ width: `${(v / max) * 75}%` }} /></RowMain>
          <RowCell width={90} align="end"><b><Roll value={v ? usd(v) : "$0"} /></b></RowCell>
        </ListRow>); })}</List>
      {!all && rows.length > 12 && <button className="link-arrow og-link" onClick={() => setAll(true)}>All {org.alderpersonsFunded} alderpersons<ArrowRight size={14} aria-hidden /></button>}
      <p className="og-caveat">Money near a vote isn’t evidence of influence. Sources: ISBE and Board of Ethics, matched by name.</p>
    </>}
    side={<>
      <WhereCard org={org} byLobbyist />
      {needsOverlapNote(org.money, view) && <section className="og-note"><h3>Totals overlap across clients</h3><p>Lobbyists' own contributions count under every client they represent. Switch to "Company only" to see what the {kindNoun(org.kind)} itself gave.</p></section>}
    </>} />;
}

function Lobbyists({ org }: { org: ClientProfile }) {
  const multi = org.lobbyists.find((l) => l.clients > 1);
  const max = Math.max(1, ...(multi?.topMembers ?? []).map((m) => m.amount));
  return <TwoCol
    main={<>
      <div className="og-lob-cards">{org.lobbyists.map((l) => (
        <div key={l.id} className="og-lob-card" data-highlight={l === multi || undefined}>
          <div className="og-lob-card__who"><span className="og-initials og-initials--lg">{l.name.split(/\s+/).map((w) => w[0]).filter((_, i, a) => i === 0 || i === a.length - 1).join("")}</span>
            <span><b>{l.name}</b><small>{l.firm}</small></span></div>
          <div className="og-lob-card__stats">
            <span><b>{l.filings}</b><small>filings</small></span>
            <span><b>{l.clients}</b><small>client{l.clients === 1 ? "" : "s"}</small></span>
            <span><b>{l.clients > 1 ? <LobbyistPeek l={l} here={org.name}>{usd(l.toAlderpersons) === "—" ? "$0" : usd(l.toAlderpersons)}</LobbyistPeek> : usd(l.toAlderpersons) === "—" ? "$0" : usd(l.toAlderpersons)}</b>
              <small>{l.toAlderpersons ? `to ${l.alderpersonsFunded} alderpersons` : "to alderpersons"}</small></span>
          </div>
        </div>))}</div>
      {multi && multi.otherClients.length > 0 && (
        <section className="og-also"><h3>{multi.name} also lobbies for</h3>
          <div className="og-pills">{multi.otherClients.slice(0, 6).map((c) => <span key={c} className="og-pill">{c}</span>)}</div>
          <a className="link-arrow og-link" href={`/lobbying/${encodeURIComponent(multi.id)}`}>All {multi.clients} clients<ArrowRight size={14} aria-hidden /></a>
        </section>
      )}
      {multi && multi.topMembers.length > 0 && (
        <section className="og-contrib">
          <div className="og-section-title"><h2>{multi.name}'s contributions</h2><span className="count-badge">{multi.alderpersonsFunded} alderpersons</span></div>
          <List variant="plain" className="og-hbars og-hbars--lobbyist">{multi.topMembers.slice(0, 6).map((m) => (
            <ListRow key={m.personId || m.name} className="og-hbars__row" href={m.personId ? personHref(m.personId, org.id) : undefined}>
              <RowCell width={172}><RowTitle className="og-hbars__name">{m.name}</RowTitle></RowCell>
              <RowMain min={40} className="og-hbars__track"><span style={{ width: `${(m.amount / max) * 60}%` }} /></RowMain>
              <RowCell width={90} align="end"><b>{usd(m.amount)}</b></RowCell>
            </ListRow>))}</List>
        </section>
      )}
    </>}
    side={multi ? <section className="og-note"><h3>Why this matters</h3><p>{whyThisMatters(org.money, multi, shortName(org.name))}</p></section> : null} />;
}

/** Filings tab (screenshots/64). Hovering a filing shows what it was matched to and how (66 #4). */
function Filings({ org }: { org: ClientProfile }) {
  const [q, setQ] = useState("");
  const years = useMemo(() => [...new Set(org.filingsRecent.map((f) => f.quarterEnding.slice(0, 4)))].sort().reverse(), [org.filingsRecent]);
  const agencies = useMemo(() => [...new Set(org.filingsRecent.map((f) => f.agency))].sort(), [org.filingsRecent]);
  const [year, setYear] = useState<string>(years[0] ?? "");
  const [agency, setAgency] = useState<string>("");
  const [quarters, setQuarters] = useState(1);
  const groups = useMemo(() => {
    const n = q.trim().toLowerCase();
    const m = new Map<string, typeof org.filingsRecent>();
    for (const f of org.filingsRecent) {
      if (year && !f.quarterEnding.startsWith(year)) continue;
      if (agency && f.agency !== agency) continue;
      if (n && !f.text.toLowerCase().includes(n)) continue;
      m.set(f.quarterEnding, [...(m.get(f.quarterEnding) ?? []), f]);
    }
    return [...m.entries()];
  }, [org.filingsRecent, q, year, agency]);
  const shown = groups.slice(0, quarters);
  const shownCount = shown.reduce((s, [, l]) => s + l.length, 0);
  const more = Math.max(0, org.filings - shownCount);
  return <TwoCol
    main={<>
      <div className="og-toolbar">
        <label className="og-search"><Search size={15} aria-hidden /><input type="search" placeholder={`Search ${org.filings} filings`} value={q} onChange={(e) => setQ(e.target.value)} aria-label={`Search ${org.filings} filings`} /><Kbd>⌘K</Kbd></label>
        <span className="og-toolbar__right">
          <Dropdown className="og-select" label="Year" align="end" icon={<Calendar size={15} aria-hidden />} chevron={false} value={year} onChange={(y) => { setYear(y); setQuarters(1); }}
            display={year} options={years.map((y) => ({ value: y, label: y }))} />
          <Dropdown className="og-select" label="Agency" align="end" icon={<Landmark size={15} aria-hidden />} chevron={false} value={agency} onChange={setAgency}
            display={agency || "All agencies"} options={[{ value: "", label: "All agencies" }, ...agencies.map((a) => ({ value: a, label: a }))]} />
        </span>
      </div>
      {shown.map(([qEnd, list]) => (
        <section key={qEnd} className="og-quarter"><span className="og-eyebrow og-eyebrow--strong">{quarterLabel(qEnd)}</span>
          {list.map((f) => (
            <div key={f.id} className="og-filing" tabIndex={0}>
              <span className="og-filing__icon"><FileText size={16} aria-hidden /></span>
              <span className="og-filing__body"><p>"{asFiled(f.text)}"</p><small>{f.agency} · filed for the quarter ending {fullDate(f.quarterEnding)}</small>
                {f.matchedRecords.length > 0 && (
                  <span className="og-filing__match">{f.matchedRecords.map((r) => (
                    <a key={r.recordId} href={`/dashboard/legislation/${encodeURIComponent(r.recordId)}`}><ScrollText size={12} aria-hidden />{r.recordId} · {r.matchedBy === "record" ? "matched by record number" : r.matchedBy === "wording" ? "matched by similar wording" : r.matchedBy === "address" ? "matched by address" : "matched by the client's name"}</a>))}</span>
                )}
              </span>
              <a className="og-filing__src" href="https://data.cityofchicago.org/d/pahz-egmi" target="_blank" rel="noreferrer">Board of Ethics</a>
            </div>))}
        </section>))}
      {more > 0 && <button className="link-arrow og-link" onClick={() => setQuarters((n) => n + 1)} disabled={quarters >= groups.length}>Earlier quarters · {more} more<ArrowRight size={14} aria-hidden /></button>}
    </>}
    side={<section className="og-card"><h3>About these filings</h3><p className="og-card__body">Registered lobbyists file quarterly reports with the Board of Ethics: who they contacted and on what. We show their wording as filed, then match it to legislation.</p></section>} />;
}

function Votes({ org, forecasts }: { org: ClientProfile; forecasts: Record<string, OrgForecastRow[]> }) {
  const counts = positionCounts(org.items);
  const choices = org.items.filter((i) => i.basis !== "unclear");
  const [record, setRecord] = useState(org.forecastItem?.recordId ?? "");
  const item = choices.find((i) => i.recordId === record);
  const basis = item?.override ? "inferred" : item?.basis ?? "unclear";
  const position = item?.override?.position ?? item?.position ?? null;
  const rows = forecasts[record] ?? [];
  const noLinked = !canComputeMoneyLinked(basis) || rows.every((r) => !r.moneyLinked.n);
  const short = shortName(org.name);
  return <TwoCol
    main={<>
      {noLinked && (
        <div className="og-banner" role="note"><Info size={16} aria-hidden />
          <div><b>No money-linked votes yet</b>
            <p>"Voted their way" needs a roll call on an item where this client stated a position.{counts.stated === 0 ? ` None of their filings state one, so forecasts below use ${item ? "the inferred position" : "no position"}${item ? " and lean on leaning, similar items and council tension instead" : ""}.` : ""}</p></div>
        </div>
      )}
      {item && (
        <Dropdown className="og-picker" label="Forecast for" value={record} onChange={setRecord}
          options={choices.map((c) => ({ value: c.recordId, label: `${c.recordId} · ${c.title}` }))}>
          <span className="og-record__icon og-record__icon--lg"><ScrollText size={14} aria-hidden /></span>
          <span className="og-picker__text"><b>Forecast for: {item.title}</b>
            <small>{[item.recordId, item.next, `position: ${position === "oppose" ? "Opposes" : "Supports"}${basis === "inferred" ? item.override ? " (team override)" : " (inferred)" : ""}`].filter(Boolean).join(" · ")}</small></span>
          <ChevronsUpDown size={16} aria-hidden />
        </Dropdown>
      )}
      {item && rows.length > 0 && (
        <table className="og-table og-table--votes">
          <thead><tr><th>Alderperson</th><th>From {short}</th><th>Money-linked</th><th>Votes their way</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.personId}>
              <td><a className="og-who" href={personHref(r.personId, org.id)} onClick={go(personHref(r.personId, org.id))}><Face name={r.name} photoUrl={r.photoUrl} size={36} /><span><b>{r.name}</b><small>Ward {r.ward}</small></span></a></td>
              <td>{usd(r.amount)}</td>
              <td className="og-muted">{canComputeMoneyLinked(basis) && r.moneyLinked.n ? `${r.moneyLinked.k} of ${r.moneyLinked.n}` : "— none yet"}</td>
              <td><ForecastMeter f={r.forecast} perspective="their" /></td>
            </tr>))}</tbody>
        </table>
      )}
      {item && rows.length > 0 && <p className="og-caveat">Forecasts using the {basis === "inferred" ? "inferred " : ""}position{noLinked ? " and no money-linked history" : ""}. Amounts are from this organization's page.</p>}
    </>}
    side={<section className="og-card"><h3>Positions we can see</h3>
      <div className="og-kv">{([["Stated in a filing", counts.stated], ["Inferred from named proposals", counts.inferred], ["Unclear", counts.unclear]] as const).map(([l, c]) => <div key={l}><span>{l}</span><b>{c}</b></div>)}</div>
      <a className="link-arrow og-link" href="/dashboard/forecast-method">How forecasts work<ArrowRight size={14} aria-hidden /></a></section>} />;
}

